import type { Messages } from "./messages.en";

/**
 * Dutch. Written as Dutch rather than translated from English word by word:
 * "a few words a day" becomes "een paar woorden per dag", and the headline is
 * cut where Dutch wants to breathe, not where English did.
 */
export const nl: Messages = {
  nav: {
    home: "SprinkleDays home",
    guides: "Gidsen",
    questions: "Vragen",
    allGuides: "Alle gidsen",
    startCourse: "Begin een cursus",
    openToday: "Open het blad van vandaag",
    howItWorks: "Hoe het werkt",
    reading: "Lezen",
    footer: "Voettekst",
    breadcrumb: "Kruimelpad",
    language: "Taal",
    languageOf: (label: string) => `Lees deze pagina in het ${label}`,
    footerBlurb:
      "SprinkleDays maakt van een lesplan één printbaar blad per dag: twee talen naast elkaar, gescheiden door een vouwlijn. Geen account, en er verlaat niets je apparaat.",
  },

  languages: {
    en: "Engels",
    nl: "Nederlands",
    th: "Thai",
    or: (names: readonly string[]) =>
      `${names.slice(0, -1).join(", ")} of ${names.at(-1)}`,
    to: (from: string, to: string) => `${from} naar ${to}`,
  },

  home: {
    headline: {
      lead: "Leer ",
      emphasis: "een paar woorden",
      tail: " per dag.",
    },
    lede: "Eén blad per dag, twee talen naast elkaar. Print de hele week voor op de keukentafel, of lees het blad van vandaag op je telefoon. Leer je samen met iemand? Vouw het blad doormidden en overhoor elkaar.",
    learning: "Dit leer je",
    speak: "Dit spreek je al",
    wordsADay: "Woorden per dag",
    sentences: "Zinnen",
    whoIsLearning: "Wie leert er",
    justMe: "Alleen ik",
    twoOfUs: "Wij samen",
    nameLeft: "Naam links",
    nameRight: "Naam rechts",
    namePlaceholderLeft: "bijv. Papa",
    namePlaceholderRight: "bijv. Nok",
    namesHint:
      "Beide namen komen boven de kolommen te staan, zodat ieder weet welke helft hij wegvouwt.",
    start: "Begin met dag 1",
    save: "Opslaan en vandaag openen",
    fine: "Geen account, geen betaalgegevens, niets op te zeggen. Alles blijft op dit apparaat.",
    sampleTag: "Eén A4 per dag",
    sampleTitle: "Zo ziet een dag eruit",
    sampleLede:
      "Je twee talen naast elkaar, met een vouwlijn door het midden. Het blad verandert mee met de instellingen hierboven.",
    sampleNotes: [
      "Vouw langs de lijn om één kant te verbergen en jezelf te overhoren",
      "Print de hele week in één keer, of dag voor dag",
      "Op het scherm tik je op een woord om te horen hoe het klinkt",
      "Scan het geprinte blad om de dag te openen en af te vinken",
    ],
    howTitle: "Drie minuten aan de keukentafel",
    steps: [
      {
        title: "Kies je twee talen",
        body: "De taal die je leert en de taal die je al spreekt. Thai, Nederlands en Engels, in elke combinatie.",
      },
      {
        title: "Print de week",
        body: "Zeven A4-bladen, één voor elke dag, beide talen naast elkaar. Of hou ze op het scherm en tik op een woord om het te horen.",
      },
      {
        title: "Vouwen en overhoren",
        body: "Vouw langs de stippellijn zodat één kant verdwijnt, en overhoor jezelf. Leer je samen? Om de beurt.",
      },
    ],
    premiumTag: "Premium",
    premiumJoinedTitle: "Je staat op de lijst.",
    premiumJoinedLede:
      "Elke week van de cursus staat voor je klaar op dit apparaat. We mailen zelden, en alleen over de cursus.",
    premiumTitleStrong: "Ga premium",
    premiumTitleRest: " en open elke week",
    premiumLede:
      "Eén cursus, of er nu één iemand uit leert of vijf. Een hele week aan bladen gaat in één keer open, klaar om in één printopdracht te printen of op je telefoon te lezen.",
    premiumAsk:
      "Laat je e-mailadres achter voor de vroege lijst, en elke week van de cursus gaat open op dit apparaat.",
    premiumSubmit: "Ga premium",
    premiumFine:
      "We versturen je e-mailadres, je twee talen en het tempo dat je instelt. Je bladen, je namen en je voortgang blijven op dit apparaat.",
    readsTag: "Voordat je begint",
    readsTitle: "Vijf minuten lezen waard",
    readsBody:
      "Hoeveel woorden per dag je moet instellen, waarom vouwen beter werkt dan herlezen, en wat een eerste maand Thai echt zou moeten behandelen.",
    readsInEnglish: "De gidsen zijn in het Engels geschreven.",
    footerBlurb:
      "Leer elke dag een beetje. Gemaakt voor iedereen die een taal oppakt, een paar woorden per keer, alleen of met de mensen om zich heen.",
  },

  articles: {
    tag: "Gidsen",
    title: "Een taal leren, een paar woorden per keer",
    minRead: (minutes: number) => `${minutes} min lezen`,
    ctaTitle: "Begin met dag 1",
    ctaBody:
      "Kies twee talen en hoeveel woorden per dag je aankunt. Het eerste blad is klaar voordat je deze zin uit hebt, en er is geen account voor nodig. Eerst nog vragen?",
    ctaLink: "Lees de antwoorden",
    categories: {
      Method: "Methode",
      Thai: "Thai",
      Dutch: "Nederlands",
      English: "Engels",
      Together: "Samen",
    },
  },

  faq: {
    tag: "Vragen",
    title: "Vragen over SprinkleDays",
    moreTitle: "Langere antwoorden",
    moreBodyBefore: "De vragen hierboven gaan over SprinkleDays. De",
    moreBodyLink: "gidsen",
    moreBodyAfter: "beantwoorden de lastigere vragen over een taal leren.",
    ctaTitle: "Niets meer te beslissen",
    ctaBody:
      "Twee talen, een aantal woorden, en dag 1. Alles blijft op dit apparaat, en het eerste blad is klaar in ongeveer een minuut.",
    ctaButton: "Begin met dag 1",
  },

  notFound: {
    title: "Deze pagina is zoekgeraakt.",
    body: "Dit adres hebben we niet. De rest staat nog waar het stond.",
    cta: "Naar het begin",
    asideBefore: "Hier beland via een zoekopdracht? De",
    asideGuides: "gidsen",
    asideAnd: "en de",
    asideQuestions: "vragenpagina",
    asideAfter: "staan er nog gewoon.",
  },

  app: {
    mainNav: "Hoofdmenu",
    today: "Vandaag",
    thisWeek: "Deze week",
    settings: "Instellingen",
    booting: "Je bladen worden geopend…",
    loading: "Laden…",
    gettingWeekReady: "De week wordt klaargezet…",
    retry: "Opnieuw proberen",
    day: (day: number) => `Dag ${day}`,
    dayOf: (day: number, total: number) => `${day} / ${total}`,
    previousDay: "Vorige dag",
    nextDay: "Volgende dag",
    backToWeek: "Terug naar deze week",
    notReleasedTitle: "Nog niet. Dat is precies de bedoeling.",
    notReleasedBody: (day: number, week: number, date: string) =>
      `Bladen komen per week binnen. Dag ${day} hoort bij week ${week}, op ${date}.`,
    finished:
      "Je bent aan het eind van de cursus. Elk blad blijft open voor nog een ronde.",
    weekTag: (words: number, days: number) =>
      `${words} woorden per dag · ${days} dagen`,
    week: (week: number) => `Week ${week}`,
    weekOf: (week: number, total: number) => `${week} / ${total}`,
    daysDone: (done: number, total: number) =>
      `${done} van ${total} dagen gedaan`,
    earlierWeek: "Eerdere week",
    laterWeek: "Latere week",
    dayLocked: (day: number, weekday: string) =>
      `Dag ${day}, ${weekday}, op slot`,
    dayLabel: (day: number, weekday: string, done: boolean) =>
      `Dag ${day}, ${weekday}${done ? ", gedaan" : ""}`,
    arriveOn: (date: string) => `Deze bladen komen binnen op ${date}.`,
    weekWaitingTitle: (week: number) => `Week ${week} staat klaar.`,
    weekWaitingBody: (day: number) =>
      `Beide talen naast elkaar, een hele week om in één keer te printen. Open dag ${day} om verder te gaan.`,
    openDay: (day: number) => `Open dag ${day}`,
    printWeek: "Print deze week",
    sheetsNote: (sheets: number) =>
      `${sheets} ${sheets === 1 ? "blad" : "bladen"}, één A4 per stuk. Vouw elk blad doormidden en vink de dag af als hij klaar is.`,
    sheetsPending: "De bladen worden klaargezet…",

    settingsTitle: "Verander wat je wilt, je voortgang blijft.",
    settingsLede: "Alles slaat vanzelf op, en blijft op dit apparaat.",
    languagesPanel: "Talen",
    leftColumn: "Linkerkolom",
    rightColumn: "Rechterkolom",
    nameAboveLeft: "Naam boven de linkerkolom",
    nameAboveRight: "Naam boven de rechterkolom",
    namePlaceholderLeft: "Optioneel, bijv. Papa",
    namePlaceholderRight: "Optioneel, bijv. Nok",
    showPronunciation: "Laat zien hoe je het Thai uitspreekt",
    pacePanel: "Hoeveel per dag",
    sentencesADay: "Zinnen per dag",
    paceNote:
      "Meer woorden per dag betekent minder dagen tot het eind van de cursus. De woorden zelf blijven in dezelfde volgorde staan.",
    coursePanel: "Jouw cursus",
    started: "Begonnen",
    timezone: "Tijdzone",
    access: "Toegang",
    fullCourse: "Hele cursus",
    restartPanel: "Opnieuw beginnen bij dag 1",
    restartBody:
      "Opnieuw beginnen houdt je talen en je tempo, en wist elke dag die je afgevinkt hebt.",
    restartConfirm: "Al je voortgang wissen?",
    restartYes: "Ja, opnieuw beginnen",
    restartNo: "Laat maar staan",
    restart: "Opnieuw beginnen",

    swapSides: "Wissel de twee kanten om",
    leftSide: "Linkerkant van het blad",
    rightSide: "Rechterkant van het blad",
    decrease: (unit: string) => `Eén ${unit} minder`,
    increase: (unit: string) => `Eén ${unit} meer`,
    unitWord: "woord",
    unitSentence: "zin",

    sheetFailed: "Het blad is niet geladen.",
    printingSheet: "Het blad van vandaag wordt klaargezet…",
    markDone: "Dag afvinken",
    notDoneYet: "Toch nog niet klaar",
    print: "Printen",
    pacePill: (words: number, sentences: number) =>
      `${words} woorden · ${sentences} ${sentences === 1 ? "zin" : "zinnen"}`,
    controlsNote:
      "Het aantal woorden per dag verandert het tempo, niet de inhoud: dezelfde lijst wordt over meer of minder dagen verdeeld.",

    email: "Je e-mailadres",
    emailPlaceholder: "jij@voorbeeld.nl",
    emailInvalid: "Dat ziet er niet uit als een e-mailadres.",
    sendFailed: "Er ging iets mis. Probeer het opnieuw.",
    sending: "Momentje…",
  },

  seo: {
    homeTitle: "SprinkleDays | Leer een taal, een paar woorden per dag",
    homeDescription:
      "Leer een taal met een paar woorden per dag, in een tempo dat je zelf kiest. Eén printbaar blad met twee talen naast elkaar, om alleen te leren of elkaar te overhoren.",
    articlesTitle:
      "Gidsen over een taal leren met een paar woorden per dag | SprinkleDays",
    articlesDescription:
      "Praktische gidsen over dagelijks oefenen: een tempo kiezen dat je volhoudt, jezelf op papier overhoren, en Thai vanaf nul beginnen.",
    faqTitle: "Vragen over SprinkleDays | SprinkleDays",
    faqDescription:
      "Wat SprinkleDays kost, welke talen erin zitten, hoe de gevouwen bladen werken, en wat er wel en niet van je apparaat af gaat.",
    homeLabel: "SprinkleDays: leer een taal, een paar woorden per dag",
    articlesLabel: "Alle gidsen over dagelijks een taal oefenen",
    faqLabel: "Vragen over SprinkleDays",
  },
};
